import {audioFields,audioSettings,isAudioAsset,validatePreparedAudio,validatePlaylistAudio} from "./playlist-audio.js";
import {validateStaticOverlays,staticOverlayFields} from "./static-overlay.js";
import { validateOverlay, validateOverlayPlaylist } from "./hotspot-overlay.js";
const format = "pirdy.transfer.v1";
const contractVersion = 1;
const mediaType = "application/vnd.pirdy.transfer+json";
const fileExtension = ".pirdytransfer";
const mebibyte = 1024 * 1024;
const allowedMediaTypes = Object.freeze(["image/jpeg", "image/png", "audio/wav"]);
const defaultLimits = Object.freeze({
  maximumFileBytes: 48 * mebibyte,
  maximumMetadataBytes: 64 * 1024,
  // Audio and overlay dependencies do not consume artwork playlist slots.
  maximumAssets: 256,
  maximumAssetBytes: 20 * mebibyte,
  maximumDecodedAssetBytes: 32 * mebibyte,
  maximumPlaylists: 16,
  maximumPlaylistItems: 16,
  maximumTotalPlaylistItems: 128,
  maximumNameCharacters: 128,
  maximumSourceDimension: 32_768,
  maximumSourcePixels: 16_000_000,
  maximumAuthoredPixels: 8_000_000,
  maximumTouchAreasPerItem: 32,
  maximumTotalTouchAreas: 512,
  maximumTouchAreaNameCharacters: 80,
  maximumCoordinateExtent: 32_768,
  maximumCoordinatePixels: 16_000_000,
});
const encoder = new TextEncoder();
const decoder = new TextDecoder("utf-8", { fatal: true });
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;
const sha256Pattern = /^[0-9a-f]{64}$/u;
const pngSignature = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

function plainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(value, names) {
  if (!plainObject(value)) return false;
  const keys = Object.keys(value).sort();
  const expected = [...names].sort();
  return keys.length === expected.length && keys.every((key, index) => key === expected[index]);
}

function limitsFor(overrides) {
  if (overrides === undefined) return defaultLimits;
  if (!plainObject(overrides)) throw new Error("The Pirdy transfer safety limits are invalid.");
  const limits = { ...defaultLimits };
  for (const [name, value] of Object.entries(overrides)) {
    if (!Object.hasOwn(defaultLimits, name) || !Number.isSafeInteger(value) || value < 1 ||
        value > defaultLimits[name]) throw new Error("The Pirdy transfer safety limits are invalid.");
    limits[name] = value;
  }
  return limits;
}

function requiredAccountId(value) {
  const accountId = typeof value === "string" ? value.trim() : "";
  if (!accountId || accountId !== value || [...accountId].length > 64 || /[\u0000-\u001f\u007f]/u.test(accountId))
    throw new Error("A valid current Pirdy account is required for a local transfer.");
  return accountId;
}

function requiredName(value, limits, label, maximum = limits.maximumNameCharacters) {
  const name = typeof value === "string" ? value.trim() : "";
  if (!name || name !== value || [...name].length > maximum || /[\u0000-\u001f\u007f]/u.test(name))
    throw new Error(label + " name is invalid.");
  return name;
}

function requiredUuid(value, label) {
  if (typeof value !== "string" || !uuidPattern.test(value) || value === "00000000-0000-0000-0000-000000000000")
    throw new Error(label + " identity is invalid.");
  return value;
}

function requiredInteger(value, minimum, maximum, message) {
  if (!Number.isSafeInteger(value) || value < minimum || value > maximum) throw new Error(message);
  return value;
}

function requiredNumber(value, minimum, maximum, message) {
  if (typeof value !== "number" || !Number.isFinite(value) || value < minimum || value > maximum)
    throw new Error(message);
  return value;
}

function requiredBoolean(value, message) {
  if (typeof value !== "boolean") throw new Error(message);
  return value;
}

function requiredEnum(value, allowed, message) {
  if (typeof value !== "string" || !allowed.includes(value)) throw new Error(message);
  return value;
}

function requiredTimestamp(value, label) {
  if (typeof value !== "string") throw new Error(label + " timestamp is invalid.");
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf()) || parsed.toISOString() !== value)
    throw new Error(label + " timestamp is invalid.");
  return value;
}

function cryptoFor(value = globalThis.crypto) {
  if (!value?.subtle || typeof value.subtle.digest !== "function")
    throw new Error("This browser cannot verify a Pirdy transfer. Use a current browser with Web Crypto support.");
  return value;
}

async function boundedBytes(value, maximumBytes, message) {
  let size;
  if (value instanceof Uint8Array) size = value.byteLength;
  else if (value instanceof ArrayBuffer) size = value.byteLength;
  else if (ArrayBuffer.isView(value)) size = value.byteLength;
  else if (typeof Blob !== "undefined" && value instanceof Blob) size = value.size;
  else throw new Error(message);
  if (size < 1 || size > maximumBytes) throw new Error(message);
  if (value instanceof Uint8Array) return Uint8Array.from(value);
  if (value instanceof ArrayBuffer) return new Uint8Array(value.slice(0));
  if (ArrayBuffer.isView(value)) return Uint8Array.from(new Uint8Array(value.buffer, value.byteOffset, value.byteLength));
  return new Uint8Array(await value.arrayBuffer());
}

function imageDimensions(type, bytes) {
  if (type === "image/png") {
    if (bytes.byteLength < 24 || !pngSignature.every((value, index) => bytes[index] === value) ||
        String.fromCharCode(...bytes.subarray(12, 16)) !== "IHDR")
      throw new Error("A Pirdy transfer PNG has an invalid signature or header.");
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const width = view.getUint32(16, false);
    const height = view.getUint32(20, false);
    if (width < 1 || height < 1) throw new Error("A Pirdy transfer PNG dimensions are invalid.");
    return { width, height };
  }
  if (type !== "image/jpeg") throw new Error("A Pirdy transfer asset media type is unsupported.");
  if (bytes.byteLength < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8 || bytes.at(-2) !== 0xff || bytes.at(-1) !== 0xd9)
    throw new Error("A Pirdy transfer JPEG has an invalid signature.");
  let offset = 2;
  while (offset + 4 <= bytes.byteLength) {
    if (bytes[offset++] !== 0xff) throw new Error("A Pirdy transfer JPEG marker stream is invalid.");
    while (offset < bytes.byteLength && bytes[offset] === 0xff) offset += 1;
    if (offset >= bytes.byteLength) break;
    const marker = bytes[offset++];
    if (marker === 0xd8 || marker === 0xd9 || (marker >= 0xd0 && marker <= 0xd7)) continue;
    if (offset + 2 > bytes.byteLength) break;
    const length = (bytes[offset] << 8) | bytes[offset + 1];
    if (length < 2 || offset + length > bytes.byteLength)
      throw new Error("A Pirdy transfer JPEG segment is invalid.");
    const sof = marker === 0xc0 || marker === 0xc1 || marker === 0xc2 || marker === 0xc3 ||
      (marker >= 0xc5 && marker <= 0xc7) || (marker >= 0xc9 && marker <= 0xcb) ||
      (marker >= 0xcd && marker <= 0xcf);
    if (sof) {
      if (length < 7) throw new Error("A Pirdy transfer JPEG frame header is invalid.");
      const height = (bytes[offset + 3] << 8) | bytes[offset + 4];
      const width = (bytes[offset + 5] << 8) | bytes[offset + 6];
      if (width < 1 || height < 1) throw new Error("A Pirdy transfer JPEG dimensions are invalid.");
      return { width, height };
    }
    offset += length;
  }
  throw new Error("A Pirdy transfer JPEG has no supported frame header.");
}

function base64Url(bytes) {
  let binary = "";
  for (let index = 0; index < bytes.length; index += 0x8000)
    binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
}

function decodeBase64Url(value, maximumBytes) {
  if (typeof value !== "string" || !value || value.includes("=") || value.length % 4 === 1 ||
      value.length > Math.ceil(maximumBytes / 3) * 4 || !/^[A-Za-z0-9_-]+$/u.test(value))
    throw new Error("A Pirdy transfer asset has invalid base64url data.");
  let binary;
  try { binary = atob(value.replaceAll("-", "+").replaceAll("_", "/") + "=".repeat((4 - value.length % 4) % 4)); }
  catch { throw new Error("A Pirdy transfer asset has invalid base64url data."); }
  if (binary.length > maximumBytes) throw new Error("A Pirdy transfer asset exceeds the safety limit.");
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  if (base64Url(bytes) !== value) {
    bytes.fill(0);
    throw new Error("A Pirdy transfer asset has non-canonical base64url data.");
  }
  return bytes;
}

function hex(bytes) {
  return [...bytes].map(value => value.toString(16).padStart(2, "0")).join("");
}

function ensurePixels(width, height, maximum, label) {
  if (width * height > maximum) throw new Error("The Pirdy transfer " + label + " exceeds the pixel safety limit.");
}

function assertCurrentAccount(expectedAccountId, getter) {
  if (!getter) return;
  if (typeof getter !== "function" || requiredAccountId(getter()) !== expectedAccountId)
    throw new Error("The signed-in Pirdy account changed before the local transfer completed.");
}

async function normalizeAsset(value, crypto, limits, serialized) {
  const fields = ["assetId", "name", "mediaType", "byteLength", "sha256",
    serialized ? "bytesBase64Url" : "bytes", "sourceWidth", "sourceHeight", "authoredWidth",
    "authoredHeight", "authoredOrientation", "authoredPlayback", "createdAtUtc", "updatedAtUtc"];
  if (!hasExactKeys(value, fields))
    throw new Error("A Pirdy transfer asset must contain only bounded portable metadata and local bytes.");
  const assetId = requiredUuid(value.assetId, "The Pirdy transfer asset");
  const name = requiredName(value.name, limits, "The Pirdy transfer asset");
  const type = requiredEnum(value.mediaType, allowedMediaTypes, "A Pirdy transfer asset media type is unsupported.");
  const byteLength = requiredInteger(value.byteLength, 1, limits.maximumAssetBytes,
    "A Pirdy transfer asset exceeds the local file safety limit.");
  if (typeof value.sha256 !== "string" || !sha256Pattern.test(value.sha256))
    throw new Error("A Pirdy transfer asset requires a lowercase SHA-256.");
  const audio=type==="audio/wav";
  const sourceWidth = requiredInteger(value.sourceWidth, audio?0:1, limits.maximumSourceDimension,
    "A Pirdy transfer source width is invalid.");
  const sourceHeight = requiredInteger(value.sourceHeight, audio?0:1, limits.maximumSourceDimension,
    "A Pirdy transfer source height is invalid.");
  ensurePixels(sourceWidth, sourceHeight, limits.maximumSourcePixels, "source image");
  const authoredWidth = requiredInteger(value.authoredWidth, audio?0:1, limits.maximumSourceDimension,
    "A Pirdy transfer authored width is invalid.");
  const authoredHeight = requiredInteger(value.authoredHeight, audio?0:1, limits.maximumSourceDimension,
    "A Pirdy transfer authored height is invalid.");
  ensurePixels(authoredWidth, authoredHeight, limits.maximumAuthoredPixels, "authored image");
  const authoredOrientation = requiredEnum(value.authoredOrientation, ["portrait", "landscape"],
    "A Pirdy transfer authored orientation is invalid.");
  const authoredPlayback = requiredEnum(value.authoredPlayback, ["static", "horizontal-scroll", "vertical-scroll", "audio"],
    "A Pirdy transfer authored playback is invalid.");
  if(audio ? (sourceWidth!==0||sourceHeight!==0||authoredWidth!==0||authoredHeight!==0||authoredPlayback!=="audio") : authoredPlayback==="audio")throw new Error("Audio metadata must use zero dimensions and audio playback.");
  const createdAtUtc = requiredTimestamp(value.createdAtUtc, "The Pirdy transfer asset creation");
  const updatedAtUtc = requiredTimestamp(value.updatedAtUtc, "The Pirdy transfer asset update");
  if (updatedAtUtc < createdAtUtc) throw new Error("A Pirdy transfer asset update precedes its creation.");
  const bytes = serialized ? decodeBase64Url(value.bytesBase64Url, limits.maximumAssetBytes) :
    await boundedBytes(value.bytes, limits.maximumAssetBytes,
      "Verified local bytes are unavailable or exceed the safety limit for a Pirdy transfer asset.");
  let digest;
  try {
    if (bytes.byteLength !== byteLength) throw new Error("A Pirdy transfer asset size does not match its local bytes.");
    const measured = audio ? (validatePreparedAudio(bytes), {width:0,height:0}) : imageDimensions(type, bytes);
    if (measured.width !== sourceWidth || measured.height !== sourceHeight)
      throw new Error("A Pirdy transfer asset dimensions do not match its embedded image.");
    digest = new Uint8Array(await crypto.subtle.digest("SHA-256", bytes));
    if (hex(digest) !== value.sha256) throw new Error("A Pirdy transfer asset failed SHA-256 verification.");
    const metadata = { assetId, name, mediaType: type, byteLength, sha256: value.sha256, sourceWidth,
      sourceHeight, authoredWidth, authoredHeight, authoredOrientation, authoredPlayback, createdAtUtc, updatedAtUtc };
    return serialized ? { ...metadata, bytes } : { ...metadata, bytesBase64Url: base64Url(bytes) };
  } finally {
    digest?.fill(0);
    if (!serialized) bytes.fill(0);
  }
}
function normalizeEffect(value, label) {
  if (!hasExactKeys(value, ["effect", "durationMs"]))
    throw new Error("A Pirdy transfer " + label + " contains unsupported or incomplete metadata.");
  return { effect: requiredEnum(value.effect, ["none", "fade"], "A Pirdy transfer effect is invalid."),
    durationMs: requiredInteger(value.durationMs, 0, 10_000, "A Pirdy transfer effect duration is invalid.") };
}

function normalizeFit(value) {
  if (!hasExactKeys(value, ["effectIn", "effectOut", "effectDurationMs", "holdDurationMs"]))
    throw new Error("Pirdy transfer Fit settings contain unsupported or incomplete metadata.");
  return { effectIn: requiredEnum(value.effectIn, ["none", "fade"], "A Pirdy transfer Fit entrance effect is invalid."),
    effectOut: requiredEnum(value.effectOut, ["none", "fade"], "A Pirdy transfer Fit exit effect is invalid."),
    effectDurationMs: requiredInteger(value.effectDurationMs, 0, 10_000, "A Pirdy transfer Fit effect duration is invalid."),
    holdDurationMs: requiredInteger(value.holdDurationMs, 100, 3_600_000, "A Pirdy transfer Fit hold duration is invalid.") };
}

function normalizeScroll(value) {
  if (!hasExactKeys(value, ["horizontalDirection", "verticalDirection", "horizontalSpeedPxPerSecond",
    "verticalSpeedPxPerSecond", "loopArtwork"]))
    throw new Error("Pirdy transfer Scroll settings contain unsupported or incomplete metadata.");
  return { horizontalDirection: requiredEnum(value.horizontalDirection, ["left", "right"],
      "A Pirdy transfer horizontal direction is invalid."),
    verticalDirection: requiredEnum(value.verticalDirection, ["up", "down"],
      "A Pirdy transfer vertical direction is invalid."),
    horizontalSpeedPxPerSecond: requiredNumber(value.horizontalSpeedPxPerSecond, 1, 60,
      "A Pirdy transfer horizontal speed is invalid."),
    verticalSpeedPxPerSecond: requiredNumber(value.verticalSpeedPxPerSecond, 5, 60,
      "A Pirdy transfer vertical speed is invalid."),
    loopArtwork: requiredBoolean(value.loopArtwork, "A Pirdy transfer loop setting is invalid.") };
}

function normalizeAction(value, assetIds, limits) {
  if (!plainObject(value)) throw new Error("A Pirdy transfer hotspot action is invalid.");
  const kind = value.kind;
  if (["next", "previous", "back", "keep-artwork"].includes(kind)) {
    if (!hasExactKeys(value, ["kind"])) throw new Error("A Pirdy transfer hotspot action has unexpected targets.");
    return { kind };
  }
  if (kind === "go-to-playlist-item") {
    if (!hasExactKeys(value, ["kind", "targetPlaylistItemId", "targetPlaylistIndex"]))
      throw new Error("A Pirdy transfer playlist-item action is incomplete.");
    return { kind, targetPlaylistItemId: requiredUuid(value.targetPlaylistItemId, "The Pirdy transfer target item"),
      targetPlaylistIndex: requiredInteger(value.targetPlaylistIndex, 0, limits.maximumPlaylistItems - 1,
        "A Pirdy transfer hotspot playlist index is invalid.") };
  }
  if (kind === "show-content" || kind === "show-qr") {
    if (!hasExactKeys(value, ["kind", "targetAssetId", "targetPlaylistItemId", "targetPlaylistIndex"]))
      throw new Error("A Pirdy transfer content action is incomplete.");
    const targetAssetId = requiredUuid(value.targetAssetId, "The Pirdy transfer target asset");
    if (!assetIds.has(targetAssetId)) throw new Error("A Pirdy transfer hotspot references content that is not embedded.");
    const itemNull = value.targetPlaylistItemId === null;
    const indexNull = value.targetPlaylistIndex === null;
    if (itemNull !== indexNull)
      throw new Error("A Pirdy transfer hotspot target identity and index must be supplied together.");
    return { kind, targetAssetId,
      targetPlaylistItemId: itemNull ? null : requiredUuid(value.targetPlaylistItemId, "The Pirdy transfer target item"),
      targetPlaylistIndex: indexNull ? null : requiredInteger(value.targetPlaylistIndex, 0,
        limits.maximumPlaylistItems - 1, "A Pirdy transfer hotspot playlist index is invalid.") };
  }
  throw new Error("A Pirdy transfer hotspot action is unsupported.");
}

function normalizeInteractions(value, assetIds, limits, counters) {
  if (value === null) return null;
  if (!hasExactKeys(value, ["touchAreas"]) || !Array.isArray(value.touchAreas) ||
      value.touchAreas.length > limits.maximumTouchAreasPerItem)
    throw new Error("A Pirdy transfer interaction set exceeds the safety limit.");
  counters.touchAreas += value.touchAreas.length;
  if (counters.touchAreas > limits.maximumTotalTouchAreas)
    throw new Error("The Pirdy transfer hotspot total exceeds the safety limit.");
  const identities = new Set();
  return { touchAreas: value.touchAreas.map(area => {
    if (!hasExactKeys(area, ["touchAreaId", "name", "coordinateMode", "coordinateSpace", "region", "action", ...(Object.hasOwn(area,"overlayImage")?["overlayImage"]:[]), ...(Object.hasOwn(area,"audio")?["audio"]:[])]))
      throw new Error("A Pirdy transfer hotspot contains unsupported or incomplete metadata.");
    const touchAreaId = requiredUuid(area.touchAreaId, "The Pirdy transfer hotspot");
    if (identities.has(touchAreaId)) throw new Error("A Pirdy transfer item contains duplicate hotspot identities.");
    identities.add(touchAreaId);
    if (!hasExactKeys(area.coordinateSpace, ["width", "height"]) ||
        !hasExactKeys(area.region, ["x", "y", "width", "height"]))
      throw new Error("A Pirdy transfer hotspot coordinate definition is incomplete.");
    const coordinateSpace = {
      width: requiredInteger(area.coordinateSpace.width, 1, limits.maximumCoordinateExtent,
        "A Pirdy transfer hotspot coordinate width is invalid."),
      height: requiredInteger(area.coordinateSpace.height, 1, limits.maximumCoordinateExtent,
        "A Pirdy transfer hotspot coordinate height is invalid.") };
    ensurePixels(coordinateSpace.width, coordinateSpace.height, limits.maximumCoordinatePixels,
      "hotspot coordinate space");
    const region = {
      x: requiredInteger(area.region.x, 0, limits.maximumCoordinateExtent,
        "A Pirdy transfer hotspot x-coordinate is invalid."),
      y: requiredInteger(area.region.y, 0, limits.maximumCoordinateExtent,
        "A Pirdy transfer hotspot y-coordinate is invalid."),
      width: requiredInteger(area.region.width, 1, limits.maximumCoordinateExtent,
        "A Pirdy transfer hotspot width is invalid."),
      height: requiredInteger(area.region.height, 1, limits.maximumCoordinateExtent,
        "A Pirdy transfer hotspot height is invalid.") };
    if (region.x + region.width > coordinateSpace.width || region.y + region.height > coordinateSpace.height)
      throw new Error("A Pirdy transfer hotspot is outside its declared coordinate space.");
    if(area.overlayImage && area.coordinateMode!=="screen")throw new Error("Overlay images require screen-fixed hotspots.");
    return { ...(area.overlayImage?{overlayImage:validateOverlay(area.overlayImage)}:{}), ...(area.audio?{audio:audioSettings(area.audio)}:{}), touchAreaId,
      name: requiredName(area.name, limits, "The Pirdy transfer hotspot", limits.maximumTouchAreaNameCharacters),
      coordinateMode: requiredEnum(area.coordinateMode, ["content", "screen"],
        "A Pirdy transfer hotspot coordinate mode is invalid."),
      coordinateSpace, region, action: normalizeAction(area.action, assetIds, limits) };
  }) };
}

function normalizePlaylists(values, assetIds, limits, assets=[]) {
  if (!Array.isArray(values) || values.length > limits.maximumPlaylists)
    throw new Error("The Pirdy transfer playlist count exceeds the safety limit.");
  const playlistIds = new Set();
  const counters = { items: 0, touchAreas: 0 };
  return values.map(value => {
    if (!hasExactKeys(value, ["playlistId", "name", "version", "createdAtUtc", "updatedAtUtc", "items"]) ||
        !Array.isArray(value.items))
      throw new Error("A Pirdy transfer playlist contains unsupported or incomplete metadata.");
    const playlistId = requiredUuid(value.playlistId, "The Pirdy transfer playlist");
    if (playlistIds.has(playlistId)) throw new Error("The Pirdy transfer contains duplicate playlists.");
    playlistIds.add(playlistId);
    if (value.items.length > limits.maximumPlaylistItems)
      throw new Error("A Pirdy transfer playlist contains too many items.");
    counters.items += value.items.length;
    if (counters.items > limits.maximumTotalPlaylistItems)
      throw new Error("The Pirdy transfer playlist item total exceeds the safety limit.");
    const itemIds = new Set();
    const items = value.items.map(item => {
      if (!hasExactKeys(item, ["playlistItemId", "assetId", "autoplay", "presentation", "transition", "fit",
        "scroll", "interactions", ...(Object.hasOwn(item,"staticOverlay")?["staticOverlay"]:[]), ...(Object.hasOwn(item,"staticOverlays")?["staticOverlays"]:[]), ...(Object.hasOwn(item,"backgroundAudio")?["backgroundAudio"]:[]), ...(Object.hasOwn(item,"audio")?["audio"]:[]), ...(Object.hasOwn(item,"touchFeedback")?["touchFeedback"]:[])]))
        throw new Error("A Pirdy transfer playlist item contains unsupported or incomplete metadata.");
      const playlistItemId = requiredUuid(item.playlistItemId, "The Pirdy transfer playlist item");
      if (itemIds.has(playlistItemId)) throw new Error("A Pirdy transfer playlist contains duplicate item identities.");
      itemIds.add(playlistItemId);
      const assetId = requiredUuid(item.assetId, "The Pirdy transfer playlist asset");
      if (!assetIds.has(assetId)) throw new Error("A Pirdy transfer playlist references content that is not embedded.");
      validateStaticOverlays(item);
      return { playlistItemId, assetId,
        autoplay: requiredBoolean(item.autoplay, "A Pirdy transfer autoplay setting is invalid."),
        presentation: requiredEnum(item.presentation, ["fit", "scroll"],
          "A Pirdy transfer playlist presentation is invalid."),
        transition: normalizeEffect(item.transition, "transition"), fit: normalizeFit(item.fit),
        scroll: normalizeScroll(item.scroll),
        interactions: normalizeInteractions(item.interactions, assetIds, limits, counters),
        ...staticOverlayFields(item), ...audioFields(item) };
    });
    validatePlaylistAudio(items,assets);
    if(items.some(item=>isAudioAsset(assets.find(asset=>asset.assetId===item.assetId))))throw new Error("Audio must be attached to artwork, not used as an image.");
    validateOverlayPlaylist(items.map(item=>({...item,interactions:{touchAreas:(item.interactions?.touchAreas??[]).map(area=>({...area,coordinateMode:area.coordinateMode==="screen"?2:1}))}})));
    for (const item of items) for (const area of item.interactions?.touchAreas ?? []) {
      const action = area.action;
      if (action.targetPlaylistIndex == null) continue;
      const target = items[action.targetPlaylistIndex];
      if (!target || target.playlistItemId !== action.targetPlaylistItemId)
        throw new Error("A Pirdy transfer hotspot playlist target is inconsistent.");
      if ((action.kind === "show-content" || action.kind === "show-qr") && action.targetAssetId !== target.assetId)
        throw new Error("A Pirdy transfer hotspot content and playlist targets disagree.");
    }
    const createdAtUtc = requiredTimestamp(value.createdAtUtc, "The Pirdy transfer playlist creation");
    const updatedAtUtc = requiredTimestamp(value.updatedAtUtc, "The Pirdy transfer playlist update");
    if (updatedAtUtc < createdAtUtc) throw new Error("A Pirdy transfer playlist update precedes its creation.");
    return { playlistId, name: requiredName(value.name, limits, "The Pirdy transfer playlist"),
      version: requiredInteger(value.version, 1, Number.MAX_SAFE_INTEGER,
        "A Pirdy transfer playlist version is invalid."), createdAtUtc, updatedAtUtc, items };
  });
}

function metadataProjection(transfer) {
  return { ...transfer, assets: transfer.assets.map(({ bytesBase64Url: _encoded, bytes: _bytes, ...asset }) => asset) };
}

function rejectDuplicateJsonKeys(text) {
  let offset = 0;
  const whitespace = () => { while (/\s/u.test(text[offset] ?? "")) offset += 1; };
  const string = () => {
    const start = offset++;
    while (offset < text.length) {
      const character = text[offset++];
      if (character === '"') return JSON.parse(text.slice(start, offset));
      if (character === "\\") offset += 1;
    }
    throw new Error();
  };
  const value = depth => {
    if (depth > 32) throw new Error();
    whitespace();
    if (text[offset] === "{") {
      offset += 1; whitespace(); const keys = new Set();
      if (text[offset] === "}") { offset += 1; return; }
      for (;;) {
        if (text[offset] !== '"') throw new Error();
        const key = string();
        if (keys.has(key)) throw new Error("duplicate");
        keys.add(key); whitespace();
        if (text[offset++] !== ":") throw new Error();
        value(depth + 1); whitespace();
        if (text[offset] === "}") { offset += 1; return; }
        if (text[offset++] !== ",") throw new Error();
        whitespace();
      }
    }
    if (text[offset] === "[") {
      offset += 1; whitespace();
      if (text[offset] === "]") { offset += 1; return; }
      for (;;) {
        value(depth + 1); whitespace();
        if (text[offset] === "]") { offset += 1; return; }
        if (text[offset++] !== ",") throw new Error();
      }
    }
    if (text[offset] === '"') { string(); return; }
    const start = offset;
    while (offset < text.length && !/[\s,\]}]/u.test(text[offset])) offset += 1;
    JSON.parse(text.slice(start, offset));
  };
  try { whitespace(); value(0); whitespace(); if (offset !== text.length) throw new Error(); }
  catch (error) {
    if (error?.message === "duplicate") throw new Error("The Pirdy transfer JSON contains duplicate fields.");
    throw new Error("The Pirdy transfer file is not valid JSON.");
  }
}
export async function createPirdyTransfer(snapshot, options = {}) {
  const limits = limitsFor(options.limits);
  const crypto = cryptoFor(options.crypto);
  if (!hasExactKeys(snapshot, ["accountId", "assets", "playlists"]))
    throw new Error("The Pirdy transfer snapshot contains unsupported or incomplete data.");
  const accountId = requiredAccountId(snapshot.accountId);
  const currentAccountId = requiredAccountId(options.currentAccountId);
  if (accountId !== currentAccountId) throw new Error("The local content belongs to a different Pirdy account.");
  assertCurrentAccount(currentAccountId, options.getCurrentAccountId);
  if (!Array.isArray(snapshot.assets) || snapshot.assets.length < 1 || snapshot.assets.length > limits.maximumAssets)
    throw new Error("A Pirdy transfer must contain a bounded selection of local content.");
  let decodedBytes = 0;
  const assets = [];
  const assetIds = new Set();
  for (const value of snapshot.assets) {
    const asset = await normalizeAsset(value, crypto, limits, false);
    assertCurrentAccount(currentAccountId, options.getCurrentAccountId);
    if (assetIds.has(asset.assetId)) throw new Error("The Pirdy transfer contains duplicate assets.");
    assetIds.add(asset.assetId);
    decodedBytes += asset.byteLength;
    if (decodedBytes > limits.maximumDecodedAssetBytes)
      throw new Error("The Pirdy transfer content total exceeds the safety limit.");
    assets.push(asset);
  }
  const playlists = normalizePlaylists(snapshot.playlists, assetIds, limits, assets);
  const now = options.now ? options.now() : new Date();
  const exportedAtUtc = now instanceof Date ? now.toISOString() : "";
  requiredTimestamp(exportedAtUtc, "The Pirdy transfer export");
  const transfer = { format, contractVersion, accountId, exportedAtUtc, assets, playlists };
  if (encoder.encode(JSON.stringify(metadataProjection(transfer))).byteLength > limits.maximumMetadataBytes)
    throw new Error("The Pirdy transfer metadata exceeds the safety limit.");
  assertCurrentAccount(currentAccountId, options.getCurrentAccountId);
  const bytes = encoder.encode(JSON.stringify(transfer));
  if (bytes.byteLength > limits.maximumFileBytes)
    throw new Error("The Pirdy transfer file exceeds the browser safety limit.");
  assertCurrentAccount(currentAccountId, options.getCurrentAccountId);
  return Object.freeze({ format, contractVersion, accountId,
    fileName: "pirdy-transfer-" + exportedAtUtc.slice(0, 10) + fileExtension,
    mediaType, bytes, assetCount: assets.length, playlistCount: playlists.length });
}

export async function parsePirdyTransferBytes(input, options = {}) {
  const limits = limitsFor(options.limits);
  const crypto = cryptoFor(options.crypto);
  const currentAccountId = requiredAccountId(options.currentAccountId);
  assertCurrentAccount(currentAccountId, options.getCurrentAccountId);
  const rawBytes = await boundedBytes(input, limits.maximumFileBytes,
    "The Pirdy transfer file is empty or exceeds the browser safety limit.");
  let text;
  try { text = decoder.decode(rawBytes); }
  catch { throw new Error("The Pirdy transfer file is not valid UTF-8 JSON."); }
  rejectDuplicateJsonKeys(text);
  let root;
  try { root = JSON.parse(text); }
  catch { throw new Error("The Pirdy transfer file is not valid JSON."); }
  if (!hasExactKeys(root, ["format", "contractVersion", "accountId", "exportedAtUtc", "assets", "playlists"]))
    throw new Error("The Pirdy transfer contains unsupported or incomplete fields.");
  if (root.format !== format || root.contractVersion !== contractVersion)
    throw new Error("The Pirdy transfer format or contract version is unsupported.");
  const accountId = requiredAccountId(root.accountId);
  if (accountId !== currentAccountId) throw new Error("This local content belongs to a different Pirdy account.");
  const exportedAtUtc = requiredTimestamp(root.exportedAtUtc, "The Pirdy transfer export");
  if (!Array.isArray(root.assets) || root.assets.length < 1 || root.assets.length > limits.maximumAssets)
    throw new Error("A Pirdy transfer must contain a bounded selection of local content.");
  const assets = [];
  const assetIds = new Set();
  let decodedBytes = 0;
  for (const value of root.assets) {
    const asset = await normalizeAsset(value, crypto, limits, true);
    assertCurrentAccount(currentAccountId, options.getCurrentAccountId);
    if (assetIds.has(asset.assetId)) throw new Error("The Pirdy transfer contains duplicate assets.");
    assetIds.add(asset.assetId);
    decodedBytes += asset.byteLength;
    if (decodedBytes > limits.maximumDecodedAssetBytes)
      throw new Error("The Pirdy transfer content total exceeds the safety limit.");
    assets.push(asset);
  }
  const playlists = normalizePlaylists(root.playlists, assetIds, limits, assets);
  const result = { format, contractVersion, accountId, exportedAtUtc, assets, playlists };
  if (encoder.encode(JSON.stringify(metadataProjection(result))).byteLength > limits.maximumMetadataBytes)
    throw new Error("The Pirdy transfer metadata exceeds the safety limit.");
  assertCurrentAccount(currentAccountId, options.getCurrentAccountId);
  return result;
}

function validatedResult(result) {
  if (!plainObject(result) || result.format !== format || result.contractVersion !== contractVersion ||
      result.mediaType !== mediaType || typeof result.fileName !== "string" ||
      !/^pirdy-transfer-\d{4}-\d{2}-\d{2}\.pirdytransfer$/u.test(result.fileName))
    throw new Error("The Pirdy transfer delivery is invalid.");
  const bytes = result.bytes instanceof Uint8Array ? result.bytes :
    result.bytes instanceof ArrayBuffer ? new Uint8Array(result.bytes) :
      ArrayBuffer.isView(result.bytes) ?
        new Uint8Array(result.bytes.buffer, result.bytes.byteOffset, result.bytes.byteLength) : null;
  if (!bytes || bytes.byteLength < 1 || bytes.byteLength > defaultLimits.maximumFileBytes)
    throw new Error("The Pirdy transfer delivery exceeds the browser safety limit.");
  return bytes;
}

export async function deliverPirdyTransfer(result, options = {}) {
  const bytes = validatedResult(result);
  const navigatorApi = options.navigator ?? globalThis.navigator;
  const FileType = options.File ?? globalThis.File;
  if (options.preferShare !== false && FileType && typeof navigatorApi?.share === "function" &&
      typeof navigatorApi?.canShare === "function") {
    const file = new FileType([bytes], result.fileName, { type: mediaType, lastModified: 0 });
    let supported = false;
    try { supported = navigatorApi.canShare({ files: [file] }) === true; } catch { supported = false; }
    if (supported) {
      await navigatorApi.share({ files: [file], title: "Pirdy transfer" });
      return Object.freeze({ method: "share", fileName: result.fileName });
    }
  }
  const documentApi = options.document ?? globalThis.document;
  const urlApi = options.URL ?? globalThis.URL;
  const BlobType = options.Blob ?? globalThis.Blob;
  if (!documentApi?.createElement || !documentApi?.body || !urlApi?.createObjectURL || !BlobType)
    throw new Error("This browser cannot share or download the Pirdy transfer.");
  const url = urlApi.createObjectURL(new BlobType([bytes], { type: mediaType }));
  try {
    const anchor = documentApi.createElement("a");
    anchor.href = url; anchor.download = result.fileName; anchor.rel = "noopener"; anchor.hidden = true;
    documentApi.body.append(anchor); anchor.click(); anchor.remove();
  } finally { (options.setTimeout ?? globalThis.setTimeout)(() => urlApi.revokeObjectURL(url), 0); }
  return Object.freeze({ method: "download", fileName: result.fileName });
}

export const pirdyTransferContract = Object.freeze({ format, contractVersion, mediaType, fileExtension,
  allowedMediaTypes, limits: defaultLimits, subscriptionRequired: false, networkRequired: false,
  authorityGrantedByFile: false });
